import { apiClient } from '../client';
import { API_CONFIG } from '../config';
import { readSelectedWorkspaceId } from '../workspaceScopedEndpoints';

function withWorkspace(): { workspace_id?: string } {
  const ws = readSelectedWorkspaceId();
  return ws ? { workspace_id: ws } : {};
}

export function resolveTemplateThumbnailUrl(s3Key: string | null | undefined): string {
  if (!s3Key) return '';
  const env = (import.meta as any).env ?? {};
  if (env.VITE_CDN_URL) {
    return `${String(env.VITE_CDN_URL).replace(/\/+$/, '')}/${s3Key}`;
  }
  const apiBase = (env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
  return `${apiBase}/wb/template-asset/${s3Key}`;
}

export interface WbSite {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  subdomain?: string;
  domain?: string;
  description?: string;
  logo_url?: string;
  favicon_url?: string;
  theme?: Record<string, any>;
  settings?: Record<string, any>;
  is_published: boolean;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

export interface WbPage {
  id: string;
  organization_id: string;
  site_id: string;
  title: string;
  slug: string;
  mode?: 'static' | 'ai-code' | 'collection-list' | 'collection-detail' | 'form';
  content?: Record<string, any>;
  published_content?: Record<string, any>;
  page_code?: string;
  compiled_bundle?: string;
  compiled_at?: string;
  data_source_id?: string;
  route_pattern?: string;
  route_param_field?: string;
  page_schema?: Record<string, any>;
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const wbSitesApi = {
  list: () =>
    apiClient.get<WbSite[]>('/wb/sites'),

  get: (siteId: string) =>
    apiClient.get<WbSite>(`/wb/sites/${siteId}`),

  create: (payload: { name: string; slug: string; domain?: string; description?: string }) =>
    apiClient.post<WbSite>('/wb/sites', payload),

  update: (siteId: string, payload: Partial<WbSite>) =>
    apiClient.put<WbSite>(`/wb/sites/${siteId}`, payload),

  delete: (siteId: string) =>
    apiClient.delete<void>(`/wb/sites/${siteId}`),
};

export const wbAiApi = {
  generate: (prompt: string) =>
    apiClient.post<{ html: string }>('/wb/ai-generate', { prompt }),

  generateStream: async (
    prompt: string,
    history: Array<{ role: 'user' | 'assistant'; content: string; isHtml?: boolean }>,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onError: (msg: string) => void,
  ) => {
    const token = localStorage.getItem('auth_token');
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
    const res = await fetch(`${baseUrl}/wb/ai-generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ prompt, history }),
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => 'Unknown error');
      onError(text);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') { onDone(); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) { onError(parsed.error); return; }
          if (parsed.content) onChunk(parsed.content);
        } catch { /* ignore malformed lines */ }
      }
    }
    onDone();
  },

  generateReactStream: async (
    prompt: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    siteId: string,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onError: (msg: string) => void,
  ) => {
    const token = localStorage.getItem('auth_token');
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
    const res = await fetch(`${baseUrl}/wb/ai-generate-react`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ prompt, history, siteId, ...withWorkspace() }),
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => 'Unknown error');
      onError(text);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') { onDone(); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) { onError(parsed.error); return; }
          if (parsed.content) onChunk(parsed.content);
        } catch { /* ignore malformed lines */ }
      }
    }
    onDone();
  },

  classifyIntent: (body: {
    prompt: string;
    activeFile?: string;
    siteHasPages?: boolean;
  }) =>
    apiClient.post<{
      intent: 'edit' | 'multi_page';
      confidence: number;
      page_hints?: string[];
      reason?: string;
    }>('/wb/ai-classify-intent', body),
};


export const wbRuntimeApi = {
  getUi: async (): Promise<string> => {
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
    try {
      const res = await fetch(`${baseUrl}/wb/runtime/wb-ui.js`, { method: 'GET' });
      if (!res.ok) return '';
      return await res.text();
    } catch {
      return '';
    }
  },
};


export interface WbAiJobStartBody {
  siteId: string;
  prompt: string;
}

export interface WbAiJobStartResponse {
  jobId: string;
  siteId: string;
  status: 'queued';
}

export type WbAiJobEvent =
  | { phase: 'queued'; site_id?: string; ts?: number }
  | { phase: 'planning'; ts?: number }
  | {
      phase: 'planned';
      pages?: Array<{ slug: string; title: string; role: string; is_home?: boolean }>;
      datastore_required?: boolean;
      ts?: number;
    }
  | { phase: 'datastore_setup'; kind?: string; name?: string; ts?: number }
  | { phase: 'datastore_ready'; ts?: number }
  | {
      phase: 'page_generating';
      page_slug: string;
      page_title?: string;
      page_role?: string;
      page_index: number;
      page_total: number;
      ts?: number;
    }
  | {
      phase: 'page_saved';
      page_slug: string;
      page_id: string;
      page_role?: string;
      page_index: number;
      page_total: number;
      is_home?: boolean;
      ts?: number;
    }
  | { phase: 'finalizing'; ts?: number }
  | {
      phase: 'completed';
      status?: 'completed';
      site_id: string;
      page_count: number;
      page_slugs: string[];
      pages?: Array<{ slug: string; page_id: string }>;
      datastore_created?: boolean;
      datastore_id?: string | null;
      first_page_slug?: string | null;
      first_page_id?: string | null;
      ts?: number;
    }
  | {
      phase: 'failed';
      reason: string;
      message?: string;
      page?: string;
      ts?: number;
    }
  | { phase: 'refused'; reason: string; message?: string; ts?: number }
  | { phase: 'cancelled'; ts?: number };

export interface WbAiJobSubscription {
  close(): void;
  readonly closed: boolean;
}

export const wbAiJobApi = {
  start: (body: WbAiJobStartBody) =>
    apiClient.post<WbAiJobStartResponse>('/wb/ai-generate-job', body),

  subscribe(
    jobId: string,
    callbacks: {
      onEvent: (event: WbAiJobEvent) => void;
      onDone: () => void;
      onError: (message: string) => void;
    },
  ): WbAiJobSubscription {
    const env = (import.meta as any).env ?? {};
    const baseUrl = (env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
    const token = localStorage.getItem('auth_token') || '';
    const ws = readSelectedWorkspaceId();
    const url = `${baseUrl}/wb/ai-generate-job/${encodeURIComponent(jobId)}/stream`
      + (ws ? `?workspace_id=${encodeURIComponent(ws)}` : '');

    let closed = false;
    const abortCtl = new AbortController();
    const handle: WbAiJobSubscription = {
      close() {
        if (closed) return;
        closed = true;
        try {
          abortCtl.abort();
        } catch {
        }
      },
      get closed() {
        return closed;
      },
    };

    (async () => {
      let res: Response;
      try {
        res = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: abortCtl.signal,
        });
      } catch (err) {
        if (closed) return;
        const msg =
          err instanceof Error && err.name === 'AbortError'
            ? 'Subscription cancelled'
            : err instanceof Error
              ? err.message
              : 'Network error';
        callbacks.onError(msg);
        handle.close();
        return;
      }

      if (!res.ok || !res.body) {
        if (closed) return;
        let detail = '';
        try {
          const raw = await res.text();
          if (raw) {
            try {
              const j = JSON.parse(raw);
              detail = j?.error?.message || j?.error || j?.message || raw;
            } catch {
              detail = raw;
            }
          }
        } catch {
          detail = '';
        }
        // eslint-disable-next-line no-console
        console.error('[wbAiJobApi.subscribe] non-OK response', { status: res.status, detail });
        callbacks.onError(detail || `HTTP ${res.status}`);
        handle.close();
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      try {
        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';
          for (const line of lines) {
            if (closed) break;
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              callbacks.onDone();
              handle.close();
              return;
            }
            try {
              const parsed = JSON.parse(data) as WbAiJobEvent;
              callbacks.onEvent(parsed);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[wbAiJobApi.subscribe] malformed SSE chunk', { data, err });
            }
          }
        }
        if (!closed) {
          callbacks.onDone();
          handle.close();
        }
      } catch (err) {
        if (closed) return;
        const msg =
          err instanceof Error && err.name === 'AbortError'
            ? 'Subscription cancelled'
            : err instanceof Error
              ? err.message
              : 'Stream read error';
        callbacks.onError(msg);
        handle.close();
      }
    })();

    return handle;
  },

  cancel: (jobId: string) =>
    apiClient.delete<{ success: true; cancelled: boolean }>(
      `/wb/ai-generate-job/${encodeURIComponent(jobId)}`,
    ),
};

export interface WbTemplateCatalogEntry {
  id: string;
  name: string;
  category: string;
  business_types: string[];
  tagline: string;
  thumbnail: string;           // S3 key, not full URL — caller resolves
  page_count: number;
  features: string[];
  version: number;
}

export interface WbBusinessInfo {
  business_name?: string;
  business_type?: string;
  services?: string;
  support_phone?: string;
  support_email?: string;
  instagram_handle?: string;
  website?: string;
}

export interface WbInstantiateBody {
  siteName?: string;
  siteSlug?: string;
  businessInfo?: WbBusinessInfo;
  addToSiteId?: string;
}

export type WbInstantiateEvent =
  | { type: 'fetching_template' }
  | { type: 'merging_ai_knowledge' }
  | { type: 'creating_site'; site: { id: string; slug: string; name?: string } }
  | { type: 'personalizing_page'; page_slug: string }
  | { type: 'saved_page'; page_slug: string; page_id: string; ai_status: string }
  | { type: 'done'; site_id: string; first_page_id: string; first_page_slug: string }
  | { type: 'error'; code: string; message: string; missing_fields?: string[] };

export const wbTemplatesApi = {
  list: () =>
    apiClient.get<WbTemplateCatalogEntry[]>('/wb/templates'),

  instantiateStream: async (
    templateId: string,
    body: WbInstantiateBody,
    onEvent: (event: WbInstantiateEvent) => void,
    onDone: () => void,
    onError: (msg: string) => void,
  ) => {
    const token = localStorage.getItem('auth_token');
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';

    let res: Response;
    try {
      res = await fetch(`${baseUrl}/wb/templates/${templateId}/instantiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ...withWorkspace(), ...body }),
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err) || 'Network error');
      return;
    }

    if (!res.ok || !res.body) {
      let detail = '';
      try {
        const raw = await res.text();
        if (raw) {
          try {
            const j = JSON.parse(raw);
            detail = j?.error?.message || j?.error || j?.message || raw;
          } catch {
            detail = raw;
          }
        }
      } catch {
        detail = '';
      }
      // eslint-disable-next-line no-console
      console.error('[wb.instantiateStream] non-OK response', { status: res.status, detail });
      onError(detail || `HTTP ${res.status} — see browser console for response body`);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') { onDone(); return; }
          try {
            const parsed = JSON.parse(data) as WbInstantiateEvent;
            if (parsed && parsed.type === 'error') {
              onError(parsed.message || parsed.code || 'Instantiate failed');
              return;
            }
            onEvent(parsed);
          } catch { /* ignore malformed lines */ }
        }
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err) || 'Stream read error');
      return;
    }
    onDone();
  },
};

export const wbPagesApi = {
  list: (siteId: string) =>
    apiClient.get<WbPage[]>(`/wb/sites/${siteId}/pages`),

  get: (siteId: string, pageId: string) =>
    apiClient.get<WbPage>(`/wb/sites/${siteId}/pages/${pageId}`),

  create: (siteId: string, payload: { title: string; slug: string; mode?: string }) =>
    apiClient.post<WbPage>(`/wb/sites/${siteId}/pages`, payload),

  update: (siteId: string, pageId: string, payload: Partial<WbPage> & { content?: any; page_code?: string; compiled_bundle?: string }) =>
    apiClient.put<WbPage>(`/wb/sites/${siteId}/pages/${pageId}`, payload),

  publish: (siteId: string, pageId: string) =>
    apiClient.post<WbPage>(`/wb/sites/${siteId}/pages/${pageId}/publish`, {}),

  unpublish: (siteId: string, pageId: string) =>
    apiClient.post<WbPage>(`/wb/sites/${siteId}/pages/${pageId}/unpublish`, {}),

  delete: (siteId: string, pageId: string) =>
    apiClient.delete<void>(`/wb/sites/${siteId}/pages/${pageId}`),
};


export interface WbChatMessage {
  role: string;
  content: string;
}

export const wbSiteChatApi = {
  getChat: (siteId: string) =>
    apiClient.get<{ messages: WbChatMessage[] }>(`/wb/sites/${siteId}/chat`),

  saveChat: (siteId: string, messages: WbChatMessage[]) =>
    apiClient.put<{ messages: WbChatMessage[] }>(`/wb/sites/${siteId}/chat`, { messages }),
};


export interface WbTemplatePageEntry {
  slug: string;
  title: string;
  is_home: boolean;
  source_s3_key: string;
}

export interface WbTemplateTokens {
  required: string[];
  optional: string[];
  defaults: Record<string, string>;
}

export interface WbTemplateAiRewrite {
  enabled: boolean;
  tone_hint: string;
  preserve_sections: string[];
}

export interface WbTemplateAdminRow {
  id: string;
  name: string;
  category: string;
  business_types: string[];
  tagline: string;
  thumbnail_s3_key: string;
  thumbnail_url: string | null;
  page_count: number;
  features: string[];
  pages: WbTemplatePageEntry[];
  tokens: WbTemplateTokens;
  ai_rewrite: WbTemplateAiRewrite;
  version: number;
  is_active: boolean;
  created_by_user_id: string | null;
  created_by_name: string | null;
  updated_by_user_id?: string | null;
  updated_by_name?: string | null;
  created_at: string;
  updated_at: string;
  duplicated_from_id?: string | null;
}

export interface WbTemplateAdminInput {
  id?: string; // omitted on update — id lives in the URL path
  name: string;
  category: string;
  business_types: string[];
  tagline: string;
  thumbnail_s3_key: string;
  features: string[];
  pages: WbTemplatePageEntry[];
  tokens: WbTemplateTokens;
  ai_rewrite: WbTemplateAiRewrite;
}

export interface WbTemplateValidateResult {
  ok: boolean;
  errors: string[];
}

export interface WbTemplateUploadResult {
  url: string;
  s3_key: string;
  size_bytes: number;
}

export interface WbAdminListParams {
  category?: string;
  status?: 'all' | 'draft' | 'published';
  q?: string;
}

export type WbAdminOk<T> = { ok: true; data: T; error: null };
export type WbAdminErr = {
  ok: false;
  data: null;
  error: string;
  code?: string;
  status?: number;
  current_updated_at?: string | null;
};
export type WbAdminResult<T> = WbAdminOk<T> | WbAdminErr;

function wbAdminOk<T>(data: T): WbAdminOk<T> {
  return { ok: true, data, error: null };
}
function wbAdminFail(error: string, extra?: Partial<WbAdminErr>): WbAdminErr {
  return { ok: false, data: null, error, ...(extra || {}) };
}

function wbAdminUnwrap<T>(response: any, fallbackError: string): WbAdminResult<T> {
  if (!response) return wbAdminFail(fallbackError);
  if (response.error || response.success === false) {
    return wbAdminFail(response.error || fallbackError);
  }
  const payload = (response.data ?? response) as T;
  return wbAdminOk<T>(payload);
}

export const wbAdminTemplatesApi = {
  async list(params?: WbAdminListParams): Promise<WbAdminResult<WbTemplateAdminRow[]>> {
    try {
      const res = await apiClient.get<WbTemplateAdminRow[]>('/admin/wb-templates', { params });
      return wbAdminUnwrap<WbTemplateAdminRow[]>(res, 'Failed to load templates');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to load templates');
    }
  },

  async get(id: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.get<WbTemplateAdminRow>(`/admin/wb-templates/${id}`);
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to load template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to load template');
    }
  },

  async create(body: WbTemplateAdminInput): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>('/admin/wb-templates', body);
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to create template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to create template');
    }
  },

  async update(
    id: string,
    body: WbTemplateAdminInput,
    ifUnmodifiedSince: string,
  ): Promise<WbAdminResult<WbTemplateAdminRow>> {
    let token = '';
    try {
      token = localStorage.getItem('auth_token') || '';
    } catch {
    }
    const url = `${API_CONFIG.BASE_URL}/admin/wb-templates/${id}`;
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'If-Unmodified-Since': ifUnmodifiedSince,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Network error');
    }
    let parsed: any = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    if (res.status === 412) {
      return wbAdminFail(
        parsed?.error || 'This template was edited by someone else.',
        {
          code: parsed?.code || 'TEMPLATE_STALE',
          status: 412,
          current_updated_at: parsed?.current_updated_at || null,
        },
      );
    }
    if (!res.ok) {
      return wbAdminFail(
        parsed?.error || parsed?.message || `HTTP ${res.status}`,
        { code: parsed?.code, status: res.status },
      );
    }
    if (parsed?.success === false) {
      return wbAdminFail(parsed?.error || 'Update failed', { code: parsed?.code });
    }
    return wbAdminOk<WbTemplateAdminRow>(parsed?.data ?? parsed);
  },

  async delete(id: string): Promise<WbAdminResult<{ success: true }>> {
    try {
      const res = await apiClient.delete<{ success: true }>(`/admin/wb-templates/${id}`);
      return wbAdminUnwrap<{ success: true }>(res, 'Failed to delete template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to delete template');
    }
  },

  async publish(id: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>(
        `/admin/wb-templates/${id}/publish`,
        {},
      );
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to publish template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to publish template');
    }
  },

  async unpublish(id: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>(
        `/admin/wb-templates/${id}/unpublish`,
        {},
      );
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to unpublish template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to unpublish template');
    }
  },

  async duplicate(id: string, newId: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>(
        `/admin/wb-templates/${id}/duplicate`,
        { new_id: newId },
      );
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to duplicate template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to duplicate template');
    }
  },

  async validate(id: string): Promise<WbAdminResult<WbTemplateValidateResult>> {
    try {
      const res = await apiClient.post<any>(`/admin/wb-templates/${id}/validate`, {});
      if (res?.error || res?.success === false) {
        return wbAdminFail(res?.error || 'Failed to validate template');
      }
      const payload: any = res?.data ?? res;
      const result: WbTemplateValidateResult = {
        ok: payload?.ok === true,
        errors: Array.isArray(payload?.errors) ? payload.errors : [],
      };
      return wbAdminOk<WbTemplateValidateResult>(result);
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to validate template');
    }
  },

  async uploadAsset(
    templateId: string,
    slot: 'thumbnail' | 'source' | 'gallery',
    file: File,
    pageSlug?: string,
  ): Promise<WbAdminResult<WbTemplateUploadResult>> {
    let token = '';
    try {
      token = localStorage.getItem('auth_token') || '';
    } catch {
    }
    const form = new FormData();
    form.append('type', slot);
    form.append('template_id', templateId);
    if (pageSlug) form.append('page_slug', pageSlug);
    form.append('file', file);

    const url = `${API_CONFIG.BASE_URL}/admin/wb-templates/upload-asset`;
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Network error');
    }
    let parsed: any = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      return wbAdminFail(
        parsed?.error || parsed?.message || `Upload failed (HTTP ${res.status})`,
        { code: parsed?.code, status: res.status },
      );
    }
    if (parsed?.success === false) {
      return wbAdminFail(parsed?.error || 'Upload failed', { code: parsed?.code });
    }
    return wbAdminOk<WbTemplateUploadResult>({
      url: parsed?.url,
      s3_key: parsed?.s3_key,
      size_bytes: parsed?.size_bytes,
    });
  },
};

export const WB_TEMPLATE_CATEGORIES = [
  'restaurant',
  'retail',
  'salon',
  'fitness',
  'coaching',
  'clinic',
  'services',
  'real_estate',
  'creator',
  'landing',
] as const;
export type WbTemplateCategory = typeof WB_TEMPLATE_CATEGORIES[number];

export const WB_TEMPLATE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
